import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Music, Disc3, MonitorPlay, Package, MapPin, Calendar } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PAPER_BASE } from './config';
import { COMPANY } from '../../data/paperCompanyInfo';
import PaperSEO from '../../components/paper/PaperSEO';
import PaperLottie from '../../components/paper/PaperLottie';
import PaperHeroCanvas from '../../components/paper/PaperHeroCanvas';
import axios from 'axios';

gsap.registerPlugin(ScrollTrigger);

const API = process.env.REACT_APP_API_URL;
const ICON_MAP = { Music, Disc3, MonitorPlay, Package };
const FALLBACK_IMG = '/images/paper/logo_pinkpaper.png';

// 파트너 로고는 원본마다 여백/글자 비율이 달라서 같은 높이로 맞춰도
// 어떤 건 유독 커 보인다. 그런 로고만 개별로 줄인다.
const PARTNER_LOGO_SCALE = {
  '안양시': 'scale-90',
  '관악구': 'scale-[0.8]',
  '강북구': 'scale-[0.8]',
  'OPCD':  'scale-[0.8]',
  '도봉구': 'scale-[0.8]',
};

const formatDateRange = (start, end) => {
  if (!start) return '';
  if (!end || end === start) return start;
  return `${start} ~ ${end}`;
};

export default function PaperHomePage() {
  const navigate = useNavigate();
  const [heroWorks, setHeroWorks] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroGlFailed, setHeroGlFailed] = useState(false);
  const [stats, setStats] = useState({ total: 89, festivals: 0, events: 0, locations: 0, partners: 0 });
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [timelineVisibleCount, setTimelineVisibleCount] = useState(2);
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);
  const timelineRef = useRef(null);
  const touchStartX = useRef(0);
  const partnerScrollRef = useRef(null);

  // Fetch latest 5 works for hero + services
  useEffect(() => {
    axios.get(`${API}/api/paperWork?limit=5&page=1`)
      .then(res => setHeroWorks(res.data.works || []))
      .catch(() => {});
    axios.get(`${API}/api/paperWork/services`)
      .then(res => setServices(res.data.services || []))
      .catch(() => {});
    axios.get(`${API}/api/paperWork/partners`)
      .then(res => setPartners(res.data.partners || []))
      .catch(() => {});
    axios.get(`${API}/api/paperWork/timeline`)
      .then(res => setTimeline(res.data.timeline || []))
      .catch(() => {});
    axios.get(`${API}/api/paperWork/stats`)
      .then(res => setStats(res.data))
      .catch(() => {});
  }, []);

  // Auto-rotate hero every 5 seconds
  useEffect(() => {
    if (heroWorks.length <= 1) return;
    const timer = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroWorks.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroWorks.length]);

  const hero = heroWorks[heroIndex] || null;

  // 캔버스에 넘길 배경 이미지 목록 (배열 아이덴티티가 매 렌더 바뀌면
  // 텍스처를 계속 다시 읽으므로 메모이즈한다)
  const heroImages = useMemo(
    () => heroWorks.map((w) => w.thumbnailUrl || FALLBACK_IMG),
    [heroWorks]
  );

  // Touch swipe handling
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setHeroIndex((i) => Math.min(heroWorks.length - 1, i + 1));
      else setHeroIndex((i) => Math.max(0, i - 1));
    }
  }, [heroWorks.length]);

  const STATS = [
    { number: stats.total, suffix: '+', label: '총 프로젝트' },
    { number: stats.festivals, suffix: '', label: '축제' },
    { number: stats.events, suffix: '', label: '공연' },
    { number: stats.locations, suffix: '', label: '활동 지역' },
    { number: new Date().getFullYear() - 2021 + 1, suffix: 'yr', label: '업력' },
  ];

  // GSAP animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.service-card', { opacity: 0, y: 60 }, {
        opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power2.out',
        scrollTrigger: { trigger: servicesRef.current, start: 'top 80%' },
      });
      gsap.fromTo('.services-title', { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.8,
        scrollTrigger: { trigger: servicesRef.current, start: 'top 85%' },
      });
      gsap.fromTo('.about-text', { opacity: 0, y: 40 }, {
        opacity: 1, y: 0, duration: 0.8,
        scrollTrigger: { trigger: aboutRef.current, start: 'top 80%' },
      });
      gsap.fromTo('.stat-item', { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.6, stagger: 0.1,
        scrollTrigger: { trigger: aboutRef.current, start: 'top 70%' },
      });
      gsap.fromTo('.timeline-year', { opacity: 0, x: -30 }, {
        opacity: 1, x: 0, duration: 0.6, stagger: 0.2, ease: 'power2.out',
        scrollTrigger: { trigger: timelineRef.current, start: 'top 80%' },
      });
      gsap.fromTo('.timeline-item', { opacity: 0, y: 20 }, {
        opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out',
        scrollTrigger: { trigger: timelineRef.current, start: 'top 75%' },
      });
    });
    return () => ctx.revert();
  }, []);

  // 카운트업 애니메이션 — stats 로드 후 실행
  useEffect(() => {
    if (!stats.total) return;
    const ctx = gsap.context(() => {
      document.querySelectorAll('.stat-number').forEach((el) => {
        const target = parseInt(el.dataset.target, 10);
        if (!target) return;
        const obj = { val: 0 };
        gsap.to(obj, {
          val: target, duration: 2, ease: 'power1.out',
          scrollTrigger: { trigger: el, start: 'top 85%' },
          onUpdate: () => { el.textContent = Math.round(obj.val); },
        });
      });
    });
    return () => ctx.revert();
  }, [stats]);

  const scrollPartner = (dir) => {
    const el = partnerScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '핑크페이퍼',
    url: 'https://pinkpaper.kr',
    logo: 'https://pinkpaper.kr/images/paper/logo_pinkpaper.png',
    description: '공연, 축제, 음원유통, 굿즈 제작',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '다산중앙로19번길 21 블루웨일 지식산업센터 1차 F동 307호',
      addressLocality: '남양주시',
      addressRegion: '경기도',
      addressCountry: 'KR',
    },
    telephone: '1844-2549',
    email: 'master@impink.co.kr',
  };

  return (
    <div className="bg-[#0d0b14]">
      <PaperSEO path="/" jsonLd={organizationJsonLd} />
      {/* ── 히어로 캐러셀 ── */}
      {heroWorks.length > 0 && hero && (
        <section className="border-b border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-6">
            <div
              className="relative overflow-hidden rounded-2xl"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}>
              {/* 배경 — WebGL(뎁스 패럴랙스 + 액체 전환), 실패 시 CSS 배경 */}
              {heroGlFailed ? (
                <div
                  className="absolute inset-0 bg-cover bg-center scale-110 transition-all duration-700"
                  style={{ backgroundImage: `url(${hero.thumbnailUrl || FALLBACK_IMG})` }}
                />
              ) : (
                <PaperHeroCanvas
                  images={heroImages}
                  index={heroIndex}
                  onUnsupported={() => setHeroGlFailed(true)}
                  className="absolute inset-0 w-full h-full"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0b14] via-[#0d0b14]/75 to-[#0d0b14]/40 pointer-events-none" />

              {/* PC 좌우 화살표 */}
              <button
                onClick={() => setHeroIndex((i) => Math.max(0, i - 1))}
                disabled={heroIndex === 0}
                className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 items-center justify-center disabled:opacity-0 transition-all">
                <ChevronLeft size={20} className="text-white" />
              </button>
              <button
                onClick={() => setHeroIndex((i) => Math.min(heroWorks.length - 1, i + 1))}
                disabled={heroIndex === heroWorks.length - 1}
                className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 items-center justify-center disabled:opacity-0 transition-all">
                <ChevronRight size={20} className="text-white" />
              </button>

              {/* 컨텐츠 */}
              <div className="relative px-5 sm:px-16 pt-52 pb-6 sm:py-16 flex items-end gap-8">
                <div className="hidden sm:block w-32 md:w-40 aspect-[210/297] rounded-2xl overflow-hidden shadow-2xl shrink-0 bg-[#1a1428]">
                  <img
                    src={hero.posterUrl || hero.thumbnailUrl || FALLBACK_IMG}
                    alt={hero.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 bg-violet-500/20 border border-violet-500/30 rounded-full text-violet-300 text-[11px] font-medium mb-3">
                    {hero.category === 'FESTIVAL' ? '축제' : hero.category === 'EVENT' ? '공연' : hero.category}
                  </span>
                  <h2 className="text-white text-2xl md:text-3xl font-bold mb-2 line-clamp-2">
                    {hero.title}
                  </h2>
                  {hero.tagline && (
                    <p className="text-white/60 text-sm mb-3 line-clamp-1">{hero.tagline}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-5">
                    {hero.workDate && (
                      <span className="flex items-center gap-1.5 text-slate-400 text-xs">
                        <Calendar size={12} className="text-violet-400/60" />
                        {formatDateRange(hero.workDate, hero.workDateEnd)}
                      </span>
                    )}
                    {hero.location && (
                      <span className="flex items-center gap-1.5 text-slate-400 text-xs">
                        <MapPin size={12} className="text-violet-400/60" />
                        {hero.location}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`${PAPER_BASE}/work/${hero.id}`)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-violet-500 hover:bg-violet-400 rounded-full font-semibold text-sm text-white transition-colors shadow-lg">
                    자세히 보기
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>

              {/* 점 인디케이터 + 모바일 좌우 버튼 */}
              <div className="relative flex items-center justify-center gap-2 pb-4">
                <button
                  onClick={() => setHeroIndex((i) => Math.max(0, i - 1))}
                  disabled={heroIndex === 0}
                  className="sm:hidden w-7 h-7 rounded-full bg-white/10 flex items-center justify-center disabled:opacity-30 transition-all shrink-0">
                  <ChevronLeft size={16} className="text-white" />
                </button>
                <div className="flex items-center gap-2">
                  {heroWorks.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setHeroIndex(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === heroIndex
                          ? 'w-5 bg-violet-400'
                          : 'w-1.5 bg-white/30 hover:bg-white/60'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setHeroIndex((i) => Math.min(heroWorks.length - 1, i + 1))}
                  disabled={heroIndex === heroWorks.length - 1}
                  className="sm:hidden w-7 h-7 rounded-full bg-white/10 flex items-center justify-center disabled:opacity-30 transition-all shrink-0">
                  <ChevronRight size={16} className="text-white" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── 파트너 로고 롤링 ── */}
      {partners.length > 0 && (() => {
        const needsScroll = partners.length >= 4;
        return (
          <section className="py-6 sm:py-10 border-b border-white/5">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 relative group/partner overflow-hidden">
              {needsScroll && (
                <>
                  <button
                    onClick={() => scrollPartner(-1)}
                    className="absolute left-0 sm:left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center opacity-0 group-hover/partner:opacity-100 transition-all">
                    <ChevronLeft size={16} className="text-white/60" />
                  </button>
                  <button
                    onClick={() => scrollPartner(1)}
                    className="absolute right-0 sm:right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center opacity-0 group-hover/partner:opacity-100 transition-all">
                    <ChevronRight size={16} className="text-white/60" />
                  </button>
                </>
              )}
              {needsScroll ? (
                <div
                  ref={partnerScrollRef}
                  className="flex items-center partner-scroll-track"
                  style={{ width: 'max-content' }}
                >
                  {[0, 1].map((setIdx) => (
                    <div key={setIdx} className="flex items-center gap-8 sm:gap-16 shrink-0 pr-8 sm:pr-16">
                      {partners.map((p) => (
                        <div key={`${p.id}-${setIdx}`} className="shrink-0 flex items-center justify-center h-8 sm:h-12">
                          {p.logoUrl ? (
                            <img
                              src={p.logoUrl}
                              alt={p.name}
                              title={p.name}
                              className={`h-6 sm:h-10 w-auto max-w-[80px] sm:max-w-[120px] object-contain opacity-40 hover:opacity-90 transition-all duration-500 ${PARTNER_LOGO_SCALE[p.name] || ''}`}
                            />
                          ) : (
                            <span className="text-white/20 text-sm font-medium whitespace-nowrap">{p.name}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-12 sm:gap-16 justify-center">
                  {partners.map((p) => (
                    <div key={p.id} className="shrink-0 flex items-center justify-center h-8 sm:h-12">
                      {p.logoUrl ? (
                        <img
                          src={p.logoUrl}
                          alt={p.name}
                          title={p.name}
                          className={`h-6 sm:h-10 w-auto max-w-[80px] sm:max-w-[120px] object-contain opacity-40 hover:opacity-90 transition-all duration-500 ${PARTNER_LOGO_SCALE[p.name] || ''}`}
                        />
                      ) : (
                        <span className="text-white/20 text-sm font-medium whitespace-nowrap">{p.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* Services (What we do) */}
      <section ref={servicesRef} className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="services-title text-center mb-20">
            <div className="flex items-center justify-center mb-3">
              <Music className="w-8 h-8 text-violet-400/60" />
            </div>
            <p className="text-violet-400/60 text-xs tracking-[0.4em] uppercase mb-3">What we do</p>
            <h2 className="text-2xl md:text-4xl font-extralight tracking-[0em] text-white">문화 콘텐츠 그룹, 핑크페이퍼</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {services.map((service) => {
              const Icon = ICON_MAP[service.icon] || Music;
              return (
                <div key={service.id} className="service-card group rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-violet-400/20 transition-all duration-500 overflow-hidden">
                  {service.imageUrl ? (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={service.imageUrl} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-violet-400/5 flex items-center justify-center">
                      <Music className="w-10 h-10 text-violet-400/20" />
                    </div>
                  )}
                  <div className="p-4 sm:p-6">
                    <h3 className="text-xs sm:text-sm font-medium text-white/90 mb-1 sm:mb-2 tracking-wide break-keep-all">{service.title}</h3>
                    <p className="text-[11px] sm:text-xs text-white/30 leading-relaxed whitespace-pre-line break-keep-all">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About / Stats */}
      <section ref={aboutRef} className="py-20 border-t border-white/5">
        <div className="max-w-6xl mx-auto text-center px-4 sm:px-6">
          <div className="about-text mb-20">
            <p className="text-violet-400/60 text-xs tracking-[0.4em] uppercase mb-3">About</p>
            <h2 className="text-2xl md:text-4xl font-extralight text-white leading-relaxed tracking-wide">{COMPANY.slogan}</h2>
            <p className="mt-6 text-white/30 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              핑크페이퍼는 공연, 전시, 축제 기획부터<br className="sm:hidden" />
              {' '}음원 유통, 방송심의, 굿즈 제작까지<br />
              문화 콘텐츠의 모든 영역을 아우르는<br className="sm:hidden" />
              {' '}종합 엔터테인먼트 기업입니다.
            </p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4 md:gap-6">
            {STATS.map((stat) => (
              <div key={stat.label} className="stat-item p-4 md:p-8 rounded-2xl border border-white/5 bg-white/[0.02]">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="stat-number text-3xl md:text-5xl font-bold text-violet-400" data-target={stat.number}>0</span>
                  {stat.suffix && <span className="text-lg md:text-2xl font-bold text-violet-400/60">{stat.suffix}</span>}
                </div>
                <p className="text-white/40 text-xs tracking-[0.15em] uppercase mt-3 font-medium whitespace-nowrap">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 프로젝트 타임라인 ── */}
      {timeline.length > 0 && (
        <section ref={timelineRef} className="py-20 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <p className="text-violet-400/60 text-xs tracking-[0.4em] uppercase mb-3">Timeline</p>
              <h2 className="text-2xl md:text-4xl font-extralight text-white">프로젝트 히스토리</h2>
            </div>

            <div className="relative">
              {/* 중앙 세로선 */}
              <div className="absolute left-4 sm:left-1/2 sm:-translate-x-px top-0 bottom-0 w-px bg-gradient-to-b from-violet-400/40 via-violet-400/20 to-transparent" />

              {timeline.slice(0, timelineVisibleCount).map((group, gi) => (
                <div key={group.year} className="mb-12 last:mb-0">
                  {/* 연도 뱃지 */}
                  <div className="timeline-year relative flex items-center mb-6">
                    <div className="absolute left-4 sm:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-violet-400 shadow-[0_0_12px_rgba(167,139,250,0.5)] z-0" />
                    <div className="ml-12 sm:ml-0 sm:absolute sm:left-1/2 sm:-translate-x-1/2 z-10">
                      <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#0d0b14] bg-opacity-90 border border-violet-400/30 text-violet-300 text-sm font-bold tracking-wider">
                        {group.year}
                        <span className="ml-2 text-violet-400/50 text-xs font-normal">{group.works.length}건</span>
                      </span>
                    </div>
                  </div>

                  {/* 프로젝트 카드들 */}
                  <div className="space-y-3">
                    {group.works.slice(0, 6).map((work, wi) => {
                      const isLeft = wi % 2 === 0;
                      return (
                        <div
                          key={work.id}
                          className={`timeline-item relative flex items-center ${isLeft ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}
                        >
                          {/* 연결 점 */}
                          <div className="absolute left-4 sm:left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-white/20" />

                          {/* 스페이서 (데스크톱) */}
                          <div className="hidden sm:block w-1/2" />

                          {/* 카드 */}
                          <div className={`ml-12 sm:ml-0 ${isLeft ? 'sm:pl-8' : 'sm:pr-8'} sm:w-1/2`}>
                            <button
                              onClick={() => navigate(`${PAPER_BASE}/work/${work.id}`)}
                              className="w-full text-left group flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.05] hover:border-violet-400/20 transition-all duration-300"
                            >
                              {work.thumbnailUrl && (
                                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-[#1a1428]">
                                  <img src={work.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-white/80 text-sm font-medium truncate group-hover:text-violet-300 transition-colors">
                                  {work.title}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${work.category === 'FESTIVAL' ? 'bg-amber-500/10 text-amber-400/70' : 'bg-violet-500/10 text-violet-400/70'}`}>
                                    {work.category === 'FESTIVAL' ? '축제' : '공연'}
                                  </span>
                                  {work.location && (
                                    <span className="text-white/25 text-[10px] truncate">{work.location}</span>
                                  )}
                                </div>
                              </div>
                              <ArrowRight size={14} className="text-white/10 group-hover:text-violet-400/50 shrink-0 transition-colors" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {group.works.length > 6 && (
                      <div className="relative flex items-center sm:justify-center">
                        <div className="ml-12 sm:ml-0">
                          <span className="text-white/20 text-xs">+{group.works.length - 6}건 더</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* 이전 연도 더 보기 */}
              {timelineVisibleCount < timeline.length && (
                <div className="flex justify-center mt-10">
                  <button
                    onClick={() => setTimelineVisibleCount(prev => prev + 1)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-white/10 text-white/40 text-xs tracking-wider hover:border-violet-400/30 hover:text-violet-300 transition-all duration-300"
                  >
                    {timeline[timelineVisibleCount]?.year}년
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6">
          <div className="flex justify-center mb-4">
            <PaperLottie type="star" size={80} loop={true} />
          </div>
          <h2 className="text-2xl md:text-4xl font-extralight text-white tracking-wide mb-8">프로젝트를 함께 만들어 보세요</h2>
          <Link to={`${PAPER_BASE}/contact`} className="inline-flex items-center gap-3 px-10 py-4 border border-violet-400/30 rounded-full text-violet-400 text-sm tracking-[0.15em] hover:bg-violet-400/10 transition-all duration-300 group">
            CONTACT US
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
